import { SocialSharing } from '@change/ds-core-ui';

import { useDeviceMessagingIcon } from '#src/app/shared/hooks/share';
import type { ShareTrackingDataOverride } from '#src/app/shared/hooks/share';

import { useResolvedLabel } from '../hooks/useResolvedLabel';

import { SOCIAL_SHARING_ICON_CONFIG_BY_CHANNEL } from './constants';
import { useRoundShareIconButton } from './hooks/useRoundShareIconButton';
import type { RoundShareIconButtonConfig } from './types';

export type { RoundShareIconButtonChannel, RoundShareIconButtonConfig, TranslatableLabel } from './types';

type Props = {
	buttonConfig: RoundShareIconButtonConfig;
	className?: string;
	onBeforeShare?: () => void;
	trackingData?: ShareTrackingDataOverride;
};

export const RoundShareIconButton = ({
	buttonConfig: { ShareAction, label, channel, successLabel },
	className,
	onBeforeShare,
	trackingData,
	...rest // for data attributes
}: Props): React.JSX.Element => {
	const { MessagingIconComponent, variant: smsVariant } = useDeviceMessagingIcon();
	const { data, actions } = useRoundShareIconButton({ label, successLabel });
	const ariaLabel = useResolvedLabel(label);
	const buttonLabel = useResolvedLabel(data.buttonLabel);

	const { icon, variant } =
		channel === 'sms'
			? { icon: <MessagingIconComponent />, variant: smsVariant }
			: SOCIAL_SHARING_ICON_CONFIG_BY_CHANNEL[channel];

	return (
		<ShareAction onBeforeAction={onBeforeShare} onAfterAction={actions.afterShare} trackingData={trackingData}>
			<SocialSharing
				icon={icon}
				label={buttonLabel}
				aria-label={ariaLabel}
				variant={variant}
				className={className}
				data-testid={`${channel}-share-button`}
				{...rest}
			/>
		</ShareAction>
	);
};
